/**
 * Widget header — RN port of the web widget's `components/header.tsx`.
 *
 * Two mutually exclusive layouts, kept as two separate trees exactly as on web:
 * the centred intro block that floats in the panel before a call, and the
 * compact bar pinned over the transcript during one. They are not one
 * parameterised layout because `ringg-header` is contractually calling-only —
 * integrators treat its presence as "a call is live" — while the intro block
 * answers to `ringg-intro`.
 */

import { useEffect, useRef, type FC } from "react";
import { Animated, Easing, Image, StyleSheet, Text, View, type StyleProp, type ViewStyle } from "react-native";
import type { PortableStyles } from "@ringg/core";

import { GradientFill, solidColor } from "../lib/gradient";
import { RinggAiIcon } from "../lib/icons";
import { toViewStyle } from "../lib/portable-styles";
import { ringgId } from "../lib/testing";
import { resolveFontFamily, useWidgetTheme } from "../lib/widget-theme";

interface HeaderProps {
  isCalling: boolean;
  isLoading: boolean;
  title: string;
  description: string;
  logoUrl?: string;
  logoStyles?: PortableStyles;
}

interface HeaderLogoProps {
  logoUrl: string | undefined;
  logoStyles: PortableStyles | undefined;
  /** Tile size and rounding — the only thing the two layouts disagree on. */
  style: StyleProp<ViewStyle>;
  /** Size of the fallback brand mark, inset inside the tile. */
  markSize: number;
}

interface StatusDotProps {
  isLoading: boolean;
  color: string;
}

/** Brand-mark sizes for the 64dp intro tile and the 36dp calling tile. */
const INTRO_MARK_SIZE = 32;
const CALLING_MARK_SIZE = 18;

/** Tailwind's `animate-pulse`: opacity 1 → .5 → 1 over 2s, ease-in-out. */
const PULSE_MIN_OPACITY = 0.5;
const PULSE_HALF_CYCLE_MS = 1000;
const PULSE_EASING = Easing.bezier(0.4, 0, 0.6, 1);

/**
 * Web frosts the calling bar — 60% transparent with `backdrop-filter: blur` —
 * so the transcript blurs through it. RN has no backdrop filter without a
 * native blur module, and 60% of a flat colour just leaves the transcript
 * legible *through* the title, so the bar is near-opaque instead: content
 * still scrolls underneath, only without the blur.
 */
const SCRIM_OPACITY = 0.96;

const HeaderLogo: FC<HeaderLogoProps> = ({ logoUrl, logoStyles, style, markSize }) => {
  const theme = useWidgetTheme();

  return (
    // <GradientFill> owns the painted node, so the contract testID rides on a
    // wrapper that shrink-wraps the tile and centres it on the cross axis (the
    // intro's `mx-auto`; a no-op inside the already-centred calling row).
    <View testID={ringgId("header-logo")} style={styles.logoSlot}>
      {/* Config styles land after the theme fill, matching web's spread order:
          a flat override replaces a solid primaryColor, a gradient one still
          paints over it (CSS `background` behaves the same way). */}
      <GradientFill color={theme.primaryColor} style={[styles.logo, style, toViewStyle(logoStyles)]}>
        {logoUrl ? (
          <Image testID={ringgId("header-logo-image")} accessibilityLabel="Logo" source={{ uri: logoUrl }} resizeMode="contain" style={styles.logoImage} />
        ) : (
          // Nothing is bundled here (web inlines its default logo as a data URI
          // at build time), so the brand mark stands in until an integrator
          // supplies logoUrl.
          <View testID={ringgId("header-logo-image")} style={styles.logoMark}>
            <RinggAiIcon size={markSize} />
          </View>
        )}
      </GradientFill>
    </View>
  );
};

const StatusDot: FC<StatusDotProps> = ({ isLoading, color }) => {
  const pulse = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (!isLoading) {
      pulse.setValue(1);
      return;
    }

    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: PULSE_MIN_OPACITY, duration: PULSE_HALF_CYCLE_MS, easing: PULSE_EASING, useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 1, duration: PULSE_HALF_CYCLE_MS, easing: PULSE_EASING, useNativeDriver: true }),
      ]),
    );
    loop.start();

    // Leaving the loop running would keep the animation driver awake for the
    // rest of the call, long after the dot has settled on "connected".
    return () => loop.stop();
  }, [isLoading, pulse]);

  return (
    // Web carries the connecting flag as a second `data-ringg-connecting`
    // attribute; RN has no attribute namespace, so the machine-readable half
    // becomes `busy` and the colour carries the visual half.
    <Animated.View testID={ringgId("header-status-dot")} accessibilityState={{ busy: isLoading }} style={[styles.statusDot, { backgroundColor: color, opacity: pulse }]} />
  );
};

const Header: FC<HeaderProps> = ({ isCalling, isLoading, title, description, logoUrl, logoStyles }) => {
  const theme = useWidgetTheme();
  const fontFamily = resolveFontFamily(theme.fontFamily);

  if (!isCalling)
    return (
      // flex-1 + centring: the logo/title/description block floats in the
      // panel's free space so the start buttons and legal text sit low.
      <View testID={ringgId("intro")} style={styles.intro}>
        <HeaderLogo logoUrl={logoUrl} logoStyles={logoStyles} style={styles.introLogo} markSize={INTRO_MARK_SIZE} />
        <View testID={ringgId("header-text")} style={styles.introText}>
          <Text testID={ringgId("header-title")} accessibilityRole="header" style={[styles.introTitle, { color: theme.textColor, fontFamily }]}>
            {title}
          </Text>
          {/* Stays up while connecting — the "Connecting..." status renders next to the loading bar instead */}
          {description ? (
            <Text testID={ringgId("header-description")} style={[styles.introDescription, { color: theme.mutedTextColor, fontFamily }]}>
              {description}
            </Text>
          ) : null}
        </View>
      </View>
    );

  return (
    <View
      testID={ringgId("header")}
      // Web puts the call status in an `sr-only` node; RN has no equivalent of
      // visually-hidden text, so the announcement rides on the bar itself and
      // the live region is what re-announces it once the call connects.
      accessibilityLabel={isLoading ? "Calling..." : "Call in Progress"}
      accessibilityLiveRegion="polite"
      style={styles.header}
    >
      <View pointerEvents="none" style={[StyleSheet.absoluteFill, styles.scrim, { backgroundColor: solidColor(theme.backgroundColor) }]} />
      <HeaderLogo logoUrl={logoUrl} logoStyles={logoStyles} style={styles.callingLogo} markSize={CALLING_MARK_SIZE} />
      <View testID={ringgId("header-text")} style={styles.headerText}>
        <View style={styles.headerTitleRow}>
          <Text testID={ringgId("header-title")} accessibilityRole="header" numberOfLines={1} style={[styles.callingTitle, { color: theme.textColor, fontFamily }]}>
            {title}
          </Text>
          <StatusDot isLoading={isLoading} color={solidColor(isLoading ? theme.mutedTextColor : theme.successColor)} />
        </View>
        {description ? (
          // Truncated on screen; web exposes the full string through a `title`
          // tooltip, which here is the accessibility label.
          <Text testID={ringgId("header-description")} accessibilityLabel={description} numberOfLines={1} style={[styles.callingDescription, { color: theme.mutedTextColor, fontFamily }]}>
            {description}
          </Text>
        ) : null}
      </View>
    </View>
  );
};

export default Header;

const styles = StyleSheet.create({
  intro: { flex: 1, justifyContent: "center", gap: 16 },
  introLogo: { width: 64, height: 64, borderRadius: 16 },
  introText: { gap: 4 },
  introTitle: { fontSize: 17, fontWeight: "500", textAlign: "center" },
  // `leading-snug` (1.375) at 14px.
  introDescription: { fontSize: 14, lineHeight: 19, fontWeight: "400", textAlign: "center", maxWidth: 210, alignSelf: "center" },

  header: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 10,
    paddingVertical: 12,
    paddingLeft: 20,
    // Clears the absolute minimize/close chrome so header content — including
    // integrator-injected brand text — never slides under it.
    paddingRight: 76,
  },
  scrim: { opacity: SCRIM_OPACITY },
  callingLogo: { width: 36, height: 36, borderRadius: 12 },
  headerText: { flexShrink: 1 },
  headerTitleRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  // flexShrink lets the title truncate instead of pushing the dot out of view.
  callingTitle: { fontSize: 15, fontWeight: "600", flexShrink: 1 },
  callingDescription: { fontSize: 13, fontWeight: "400" },
  statusDot: { width: 6, height: 6, borderRadius: 3, flexShrink: 0 },

  logoSlot: { alignSelf: "center", flexShrink: 0 },
  logo: { alignItems: "center", justifyContent: "center", overflow: "hidden" },
  logoImage: { width: "100%", height: "100%" },
  logoMark: { alignItems: "center", justifyContent: "center" },
});
